import type {ActionsObservable} from 'redux-observable';
import {Observable} from 'rxjs';
import {switchMap, filter, map, catchError} from 'rxjs/operators';

import {transformDataForReport} from 'nutshell-core/json-to-report-data';

import type {ChatReportDataRequestedAction, ChatbotReportDataRequestedAction} from '../types';
import {getReportPostParams} from '../utils';
import type {ReportPostParams} from '../utils';

import {
    updateReportChartData,
    failReportChartData,
    updateChatbotReportChartData,
    failChatbotReportChartData,
} from './chat-actions';

export const requestChatReportDataEpic = (action$: ActionsObservable<any>) =>
    action$.ofType('CHAT_REPORT_DATA_REQUESTED').pipe(
        switchMap((action: ChatReportDataRequestedAction) => {
            const {reportParams, filters} = action.payload;
            const params = getReportPostParams(reportParams, filters);
            const requestStream = Observable.from(fetchData(params));
            const reportDataStream = requestStream.pipe(map(transformDataForReport));

            const reportChartDataStream = reportDataStream.pipe(
                map((data) => data.chartData),
                filter((chartData) => Boolean(chartData)),
                map((chartData) => updateReportChartData({chartData}))
            );

            return reportChartDataStream.pipe(
                catchError((err) => Observable.of(failReportChartData(err)))
            );
        })
    );

export const requestChatbotReportDataEpic = (action$: ActionsObservable<any>) =>
    action$.ofType('CHATBOT_REPORT_DATA_REQUESTED').pipe(
        switchMap((action: ChatbotReportDataRequestedAction) => {
            const {reportParams, filters} = action.payload;
            const params = getReportPostParams(reportParams, filters);
            const requestStream = Observable.from(fetchChatbotData(params));
            const reportDataStream = requestStream.pipe(map(transformDataForReport));

            const reportChartDataStream = reportDataStream.pipe(
                map((data) => data.chartData),
                filter((chartData) => Boolean(chartData)),
                map((chartData) => updateChatbotReportChartData({chartData}))
            );

            return reportChartDataStream.pipe(
                catchError((err) => Observable.of(failChatbotReportChartData(err)))
            );
        })
    );

function fetchChatbotData(params: ReportPostParams) {
    return Promise.all([fetchChatbotReportData(params)]);
}

function fetchData(params: ReportPostParams) {
    return Promise.all([fetchChatReportData(params)]);
}

function fetchChatReportData(params: ReportPostParams): Promise<any> {
    const url = '/rest/channelreports/report';
    const ajaxParams = {
        url,
        dataType: 'json',
        data: {...params, channelType: 'chat'},
    } as const;

    return $.ajax(ajaxParams) as any; // This is how we have to convince flow that the response is a ReportsResponse
}

function fetchChatbotReportData(params: ReportPostParams): Promise<any> {
    const url = '/rest/channelreports/report';
    const ajaxParams = {
        url,
        dataType: 'json',
        data: {...params, channelType: 'chatbot'},
    } as const;

    return $.ajax(ajaxParams) as any; // This is how we have to convince flow that the response is a ReportsResponse
}
